import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

export interface ProductRelationOption {
    id: number;
    title: string;
}

interface ProductRelationAutocompleteProps<
    T = ProductRelationOption,
    M extends boolean = false,
    DC extends boolean = false,
    FS extends boolean = false,
> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: ProductRelationOption) => T;
    /** When set, filters this list locally instead of calling the API (e.g. import preload). */
    cachedRelationTypes?: ProductRelationOption[];
    [key: string]: unknown;
}

const ProductRelationAutocomplete = <
    T = ProductRelationOption,
    M extends boolean = false,
    DC extends boolean = false,
    FS extends boolean = false,
>({
    textFieldProps = {},
    form = false,
    name = undefined,
    required = false,
    setLoadingOptions: setLoadingOptionsProps,
    transformRecord,
    cachedRelationTypes,
    ...props
}: ProductRelationAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    const getOptions = useCallback(async (_cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            const kw = (keywords ?? '').trim().toLowerCase();
            const list = (cachedRelationTypes ?? []).map(
                (entry) => transformRecord?.(entry) ?? (entry as T),
            );
            if (!kw) {
                return list;
            }
            return list.filter((item) => {
                const title = (item as ProductRelationOption)?.title ?? '';
                return title.toLowerCase().includes(kw);
            });
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [cachedRelationTypes, isMounted, transformRecord]);

    const isOptionEqualToValue = useCallback(
        (option: ProductRelationOption, value: ProductRelationOption | null) =>
            option.id === value?.id,
        [],
    );

    const getOptionLabel = useCallback(
        (option: ProductRelationOption | AutocompleteFreeSoloValueMapping<FS>) =>
            props.freeSolo === true ? option : `${(option as ProductRelationOption)?.title ?? ''}`,
        [props.freeSolo],
    );

    const getOptionKey = useCallback(
        (option: ProductRelationOption | AutocompleteFreeSoloValueMapping<FS>) =>
            props.freeSolo === true ? option : String((option as ProductRelationOption)?.id ?? ''),
        [props.freeSolo],
    );

    const defaultOptions = useMemo(() => [], []);
    const useCachedRelationTypes = !!cachedRelationTypes?.length;

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased={!useCachedRelationTypes}
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={useCachedRelationTypes ? 0 : 1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default ProductRelationAutocomplete;
